import { CopyOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Badge, Button, Checkbox, DatePicker, Divider, Empty, Tag } from 'antd';
import { filter, forEach, isEmpty, map } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PLAN_NAMES_OPTIONS,
  PLAN_TYPES_OPTIONS,
  REFERRAL_DESCRIPTION,
  REFERRAL_PAYMENT_STATUS,
  REFERRAL_TYPE,
  ROUTES,
  SORT,
  STATUS_FILTER,
  TABLE_FILTER_KEYS,
  TRANSACTION_HISTORY_TYPES,
} from '../../../../../common/constants';
import {
  formatDate,
  handleCopy,
  handleOpenUserDrawer,
  toDayjsRange,
} from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../components/SearchComponent';
import useRetainedTableState from '../../../../../hooks/useRetainedTableFilterState';
import { TRANSACTION_HISTORIES_REFERRAL } from '../graphql/Queries';

const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialInflowFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialInflowSort = {
  field: 'createdAt',
  order: 'DESC',
};

const ReferralTable = ({
  exportFilter,
  userId,
  setExportFilter,
  transactionTypes,
  referredByType,
}) => {
  const {
    dispatch,
    state: {
      commonPermissions: { isUserPermission, isLibraryPermission },
    },
  } = useContext(AppContext);
  const history = useHistory();
  const [referralList, setReferralList] = useState([]);
  const [referralSearchTerm, setInflowSearchTerm] = useState('');
  const [isEmptyReferralList, setIsEmptyReferralList] = useState(false);
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isReferralLoading, setIsReferralLoading] = useState(true);
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState({});
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState({});
  const [finalFilterTags, setFinalFilterTags] = useState(null);

  const [initializedFromRetained, setInitializedFromRetained] = useState(false);
  const {
    hydrated,
    retainedState,
    saveRetainedFilterState,
  } = useRetainedTableState();
  const finalWhereFiltersRef = useRef(null);
  const sortedInfoRef = useRef(null);
  const paginationPropRef = useRef(null);
  const adminSearchTermRef = useRef(null);

  useEffect(() => {
    if (!hydrated) return;
    if (initializedFromRetained) return;

    const retainedFiters =
      retainedState?.[
        referredByType === REFERRAL_TYPE?.USER
          ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
          : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL
      ];

    if (!retainedFiters) {
      setInitializedFromRetained(true);
      return;
    }
    const baseFilters = retainedFiters?.filters || {};
    const normalizedFilters = {
      ...baseFilters,
      createdAt: toDayjsRange(baseFilters?.createdAt),
    };
    setFinalWhereFilters(normalizedFilters);
    finalWhereFiltersRef.current = normalizedFilters;
    setFilterTags(retainedFiters?.filterTags || {});
    setFinalFilterTags(retainedFiters?.filterTags || {});
    setSortedInfo(retainedFiters?.sort);
    sortedInfoRef.current = retainedFiters?.sort;

    setPaginationProp(retainedFiters?.paginationProp);
    paginationPropRef.current = retainedFiters?.paginationProp;

    setInflowSearchTerm(retainedFiters?.searchTerm);
    adminSearchTermRef.current = retainedFiters?.searchTerm;
    setQuery(retainedFiters?.searchTerm);
    setInitializedFromRetained(true);
  }, [hydrated, initializedFromRetained, retainedState]);

  const [transactionHistories] = useLazyQuery(TRANSACTION_HISTORIES_REFERRAL, {
    onCompleted: (response) => {
      setReferralList([...response?.transactionHistories?.data]);
      if (
        response?.transactionHistories?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyReferralList(true);
      } else {
        setIsEmptyReferralList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.transactionHistories?.count,
      };
      saveRetainedFilterState(
        referredByType === REFERRAL_TYPE?.USER
          ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
          : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL,
        {
          paginationProp: pagination,
        },
      );
      setPaginationProp(pagination);
      setIsReferralLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    if (!hydrated || !initializedFromRetained) return; // NEW
    setPaginationProp({
      ...paginationProp,
      current: paginationPropRef?.current?.current,
      skip:
        (paginationPropRef?.current?.current - 1) *
        (paginationPropRef?.current?.pageSize || 0),
    });
    saveRetainedFilterState(
      referredByType === REFERRAL_TYPE?.USER
        ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
        : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL,
      {
        paginationProp: {
          ...paginationProp,
          current: paginationPropRef?.current?.current,
          skip:
            (paginationPropRef?.current?.current - 1) *
            (paginationPropRef?.current?.pageSize || 0),
        },
      },
    );
    setIsReferralLoading(true);
    transactionHistories({
      variables: {
        where: userId && {
          id: userId,
        },
        filter: {
          ...initialInflowFilter,
          skip:
            (paginationPropRef?.current?.current - 1) *
              (paginationPropRef?.current?.pageSize || 0) || 0,

          limit: paginationPropRef?.current?.pageSize,
          search: referralSearchTerm,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          ...(referredByType && {
            referredByType,
          }),
          transactionTypes:
            finalWhereFiltersRef?.current?.description?.flat()?.length > 0
              ? finalWhereFiltersRef?.current?.description?.flat()
              : transactionTypes,
          types:
            finalWhereFiltersRef?.current?.billingCycle?.length > 0
              ? finalWhereFiltersRef?.current?.billingCycle
              : undefined,
          names:
            finalWhereFiltersRef?.current?.planSubscribed?.length > 0
              ? finalWhereFiltersRef?.current?.planSubscribed
              : undefined,
          status:
            finalWhereFiltersRef?.current?.status?.length > 0
              ? finalWhereFiltersRef?.current?.status
              : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialInflowSort,
      },
    });
  }, [hydrated, initializedFromRetained]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsReferralLoading(true);
    setSortedInfo(sorter);
    saveRetainedFilterState(
      referredByType === REFERRAL_TYPE?.USER
        ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
        : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL,
      {
        paginationProp: { ...paginationProp, ...pagination },
        sort: sorter,
      },
    );
    transactionHistories({
      variables: {
        where: userId && {
          id: userId,
        },
        filter: {
          ...initialInflowFilter,
          skip,
          limit: pagination?.pageSize,
          search: referralSearchTerm,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          ...(referredByType && {
            referredByType,
          }),
          transactionTypes:
            finalWhereFilters?.description?.flat()?.length > 0
              ? finalWhereFilters?.description?.flat()
              : transactionTypes,
          types:
            finalWhereFilters?.billingCycle?.length > 0
              ? finalWhereFilters?.billingCycle
              : undefined,
          names:
            finalWhereFilters?.planSubscribed?.length > 0
              ? finalWhereFilters?.planSubscribed
              : undefined,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialInflowSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setInflowSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsReferralLoading(true);
    saveRetainedFilterState(
      referredByType === REFERRAL_TYPE?.USER
        ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
        : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL,
      {
        paginationProp: { ...paginationProp, current: 1, skip: 0 },
        searchTerm: trimValue,
      },
    );
    transactionHistories({
      variables: {
        where: userId && {
          id: userId,
        },
        filter: {
          ...initialInflowFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          ...(referredByType && {
            referredByType,
          }),
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          transactionTypes:
            finalWhereFilters?.description?.flat()?.length > 0
              ? finalWhereFilters?.description?.flat()
              : transactionTypes,
          types:
            finalWhereFilters?.billingCycle?.length > 0
              ? finalWhereFilters?.billingCycle
              : undefined,
          names:
            finalWhereFilters?.planSubscribed?.length > 0
              ? finalWhereFilters?.planSubscribed
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialInflowSort,
      },
    });
    if (exportFilter) {
      setExportFilter({ ...exportFilter, search: trimValue });
    }
  };

  const getFilterData = (confirm) => {
    setIsReferralLoading(true);
    transactionHistories({
      variables: {
        where: userId && {
          id: userId,
        },
        filter: {
          ...initialInflowFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: referralSearchTerm,
          ...(referredByType && {
            referredByType,
          }),
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          status:
            whereFilter?.status?.length > 0 ? whereFilter?.status : undefined,
          transactionTypes:
            whereFilter?.description?.flat()?.length > 0
              ? whereFilter?.description?.flat()
              : transactionTypes,
          types:
            whereFilter?.billingCycle?.length > 0
              ? whereFilter?.billingCycle
              : undefined,
          names:
            whereFilter?.planSubscribed?.length > 0
              ? whereFilter?.planSubscribed
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialInflowSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    if (exportFilter) {
      setExportFilter({
        ...exportFilter,
        ...finalWhereFilters,
        ...whereFilter,
      });
    }
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    saveRetainedFilterState(
      referredByType === REFERRAL_TYPE?.USER
        ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
        : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL,
      {
        filters: { ...finalWhereFilters, ...whereFilter },
        paginationProp: { ...paginationProp, skip: 0, current: 1 },
        filterTags,
      },
    );
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    if (exportFilter) {
      setExportFilter({
        ...exportFilter,
        ...finalWhereFilters,
        ...whereFiltersCopy,
      });
    }
    setIsReferralLoading(true);
    transactionHistories({
      variables: {
        where: userId && {
          id: userId,
        },
        filter: {
          ...initialInflowFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: referralSearchTerm,
          ...(referredByType && {
            referredByType,
          }),
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : undefined,
          transactionTypes:
            whereFiltersCopy?.description?.flat()?.length > 0
              ? whereFiltersCopy?.description?.flat()
              : transactionTypes,
          types:
            whereFiltersCopy?.billingCycle?.length > 0
              ? whereFiltersCopy?.billingCycle
              : undefined,
          names:
            whereFiltersCopy?.planSubscribed?.length > 0
              ? whereFiltersCopy?.planSubscribed
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialInflowSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    saveRetainedFilterState(
      referredByType === REFERRAL_TYPE?.USER
        ? TABLE_FILTER_KEYS.PAYMENTS_END_USER_REFERRAL
        : TABLE_FILTER_KEYS.PAYMENTS_LIBRARY_REFERRAL,
      {
        filters: { ...finalWhereFilters, ...whereFiltersCopy },
        filterTags: { ...filterTags, ...tagsCopy },
        paginationProp: { ...paginationProp, skip: 0, current: 1 },
      },
    );
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(STATUS_FILTER, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'description':
          forEach(REFERRAL_DESCRIPTION, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'billingCycle':
          forEach(PLAN_TYPES_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'planSubscribed':
          forEach(PLAN_NAMES_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent spinning={filterLoading} setHeight={35}>
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div className="filter-checkboxes">
              {filterList?.length > 0 ? (
                map(filterList, (item) => (
                  <div className="single-checkbox-div" key={item?.key}>
                    <Checkbox
                      value={item}
                      checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                      key={item?.key || item}
                      onChange={(e) => changeFilter(e, dataIndex)}
                      className="single-checkbox"
                    >
                      <span>{item?.label}</span>
                    </Checkbox>
                  </div>
                ))
              ) : (
                <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'TRANSACTION NUMBER',
      dataIndex: 'transactionNumber',
      key: 'transactionNumber',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.transactionNumber || '-',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
    },
    ...(referredByType === REFERRAL_TYPE?.USER
      ? [
          {
            title: 'USER NAME',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            width: 250,
            align: 'left',
            className: `max-width-column`,
            onCell: (record) => ({
              onClick: (e) =>
                isUserPermission &&
                record?.user?.id &&
                handleOpenUserDrawer(e, record?.user, dispatch),

              className: `${
                isUserPermission && record?.user?.id && 'pointer table-cell'
              }`,
            }),
            render: (_, record) => {
              const userData = record?.user;
              return (
                <>
                  {userData?.firstName || userData?.lastName ? (
                    <div className="table-data-with-id">{`${
                      userData?.firstName ? userData?.firstName : ''
                    } ${userData?.lastName ? userData?.lastName : ''}`}</div>
                  ) : (
                    '-'
                  )}
                  {userData?.userNumber ? (
                    <div className="font-size-12">({userData?.userNumber})</div>
                  ) : (
                    ''
                  )}
                </>
              );
            },
          },
        ]
      : []),
    ...(referredByType === REFERRAL_TYPE?.LIBRARY
      ? [
          {
            title: 'LIBRARY NAME',
            dataIndex: 'libraryName',
            key: 'libraryName',
            width: 250,
            align: 'left',
            ellipsis: true,
            className: `max-width-column`,
            onCell: (record) => ({
              onClick: () =>
                isLibraryPermission &&
                record?.metaData?.referredLibrary?.id &&
                history?.push(
                  `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.metaData?.referredLibrary?.id}/${record?.metaData?.referredLibrary?.libraryNumber}`,
                ),
              className: `${
                isLibraryPermission &&
                record?.metaData?.referredLibrary?.id &&
                'pointer table-cell'
              }`,
            }),
            render: (_, record) => {
              const libraryData = record?.metaData?.referredLibrary;
              return (
                <>
                  <div className="table-data-with-id">{libraryData?.name}</div>
                  {libraryData?.libraryNumber ? (
                    <div className="font-size-12">
                      ({libraryData?.libraryNumber})
                    </div>
                  ) : (
                    '-'
                  )}
                </>
              );
            },
          },
        ]
      : []),
    {
      title: 'REFERRED USER NAME',
      dataIndex: 'referredUser',
      key: 'referredUser',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: (e) =>
          isUserPermission &&
          record?.metaData?.referredUser?.id &&
          handleOpenUserDrawer(e, record?.metaData?.referredUser, dispatch),
        className: `${
          isUserPermission &&
          record?.metaData?.referredUser?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.metaData?.referredUser;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div>{`${userData?.firstName ? userData?.firstName : ''} ${
                userData?.lastName ? userData?.lastName : ''
              }`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'GENERATED ON',
      dataIndex: 'generatedOn',
      key: 'generatedOn',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR),
      ...filterPopup('createdAt'),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => TRANSACTION_HISTORY_TYPES?.[record?.type]?.key,
      ...(referredByType !== REFERRAL_TYPE?.LIBRARY &&
        filterPopup('description')),
    },
    ...(referredByType === REFERRAL_TYPE?.LIBRARY
      ? [
          {
            title: 'REFERRAL CODE',
            dataIndex: 'code',
            key: 'code',
            ellipsis: true,
            width: 250,
            align: 'left',
            className: 'max-width-column code-cell',
            render: (_, record) => {
              const referralCode =
                record?.metaData?.referredLibrary?.referralCode;
              return referralCode ? (
                <div>
                  <span className="pr-8">{referralCode}</span>
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => {
                      handleCopy(referralCode);
                    }}
                  />
                </div>
              ) : (
                '-'
              );
            },
          },
        ]
      : []),
    ...(referredByType !== REFERRAL_TYPE?.LIBRARY
      ? [
          {
            title: 'STATUS',
            dataIndex: 'status',
            key: 'status',
            ellipsis: true,
            width: 150,
            align: 'left',
            className: 'max-width-column',
            render: (_, record) =>
              record?.type === TRANSACTION_HISTORY_TYPES?.REFERRAL?.value ? (
                '-'
              ) : (
                <Badge
                  color={REFERRAL_PAYMENT_STATUS?.[record?.status]?.color}
                  text={REFERRAL_PAYMENT_STATUS?.[record?.status]?.text}
                />
              ),
          },
        ]
      : []),
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      key: 'amount',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.price || '-',
    },
  ];

  const locale = {
    emptyText: isEmptyReferralList ? '' : <span />,
  };

  return (
    <>
      <div className="d-flex justify-end mb-16 flex-wrap">
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={referralList || []}
        loading={isReferralLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default ReferralTable;
